import debounce from "lodash/debounce";
import { IFRAME_ID } from "../Frames/constants";
import { detectFlexDirection } from "../Helpers/detectFlexDirection";
import { isBlockCategoryNameArray } from "../Helpers/typePredicates";
import type { DropZonePosition } from "../Store/types";

const DEBOUNCE_TIME = 200;

type SetDropZonePositions = (positions: DropZonePosition[]) => void;

interface MeasurableElement extends HTMLElement {
  dataset: {
    parentPath: string;
    blockIndex?: string;
    accepts: string;
    rejects: string;
  };
}

const isMeasurableElement = (
  element: Element,
): element is MeasurableElement => {
  if (
    !element.ownerDocument.defaultView ||
    !(element instanceof element.ownerDocument.defaultView.HTMLElement)
  ) {
    return false;
  }
  const { dataset } = element;
  return (
    typeof dataset.parentPath === "string" &&
    typeof dataset.accepts === "string" &&
    typeof dataset.rejects === "string"
  );
};

const safeCategoryParse = (acceptsString: string, rejectsString: string) => {
  try {
    const accepts: unknown = JSON.parse(acceptsString);
    const rejects: unknown = JSON.parse(rejectsString);

    if (
      !isBlockCategoryNameArray(accepts) ||
      !isBlockCategoryNameArray(rejects)
    ) {
      return { accepts: [], rejects: [] };
    }

    return { accepts, rejects };
  } catch (error) {
    console.error(error);
    return { accepts: [], rejects: [] };
  }
};

const findLayoutContainer = (element: Element): Element | null => {
  let container: Element | null = element.parentElement;
  while (container) {
    const styles =
      container.ownerDocument?.defaultView?.getComputedStyle(container);
    if (
      styles &&
      (styles.display === "flex" ||
        styles.display === "inline-flex" ||
        styles.display === "grid")
    ) {
      break;
    }
    container = container.parentElement;
  }
  return container;
};

const getDropZoneOrientation = (
  element: Element,
): "horizontal" | "vertical" => {
  const container = findLayoutContainer(element);
  if (!container) return "horizontal";

  const styles =
    container.ownerDocument?.defaultView?.getComputedStyle(container);
  if (!styles) return "horizontal";

  if (styles.display === "grid") {
    return "vertical";
  }

  return detectFlexDirection(container);
};

const getEmptyListDropZones = (
  listContainers: Element[],
): DropZonePosition[] => {
  const positions: DropZonePosition[] = [];
  listContainers.forEach(element => {
    const listElements = Array.from(
      element.querySelectorAll("[data-measurable-block]"),
    );

    if (!isMeasurableElement(element) || listElements.length > 0) {
      return;
    }

    const parentElement = element.parentElement;
    if (!parentElement) return;

    const parentRect = parentElement.getBoundingClientRect();
    const parentStyles = window.getComputedStyle(parentElement);
    const paddingLeft = parseFloat(parentStyles.paddingLeft);
    const paddingRight = parseFloat(parentStyles.paddingRight);

    const { dataset } = element;
    const { accepts, rejects } = safeCategoryParse(
      dataset.accepts,
      dataset.rejects,
    );

    const orientation = getDropZoneOrientation(parentElement);

    const isVertical = orientation === "vertical";
    const height = isVertical ? parentRect.height : 16;
    const width = isVertical
      ? 16
      : parentRect.width - paddingLeft - paddingRight;
    const top = parentRect.top;
    const left = isVertical
      ? parentRect.left - 1
      : parentRect.left + paddingLeft;

    positions.push({
      top,
      left,
      width: width > 0 ? width : 10,
      height,
      path: dataset.parentPath,
      index: 0,
      accepts,
      rejects,
      orientation,
    });
  });

  return positions;
};

const getTopDropZones = (listContainers: Element[]): DropZonePosition[] => {
  const positions: DropZonePosition[] = [];
  listContainers.forEach(element => {
    const listElements = Array.from(
      element.querySelectorAll("[data-measurable-block]"),
    );

    if (!isMeasurableElement(element) || listElements.length === 0) {
      return;
    }

    const firstChildRect = listElements[0].getBoundingClientRect();
    const { dataset } = element;
    const { accepts, rejects } = safeCategoryParse(
      dataset.accepts,
      dataset.rejects,
    );

    const orientation = getDropZoneOrientation(element);

    const isVertical = orientation === "vertical";
    const top = isVertical ? firstChildRect.top : firstChildRect.top - 8;
    const left = isVertical ? firstChildRect.left - 1 : firstChildRect.left;
    const width = isVertical ? 16 : firstChildRect.width || 10;
    const height = isVertical ? firstChildRect.height : 16;

    positions.push({
      top,
      left,
      width,
      height,
      path: dataset.parentPath,
      index: 0,
      accepts,
      rejects,
      orientation,
    });
  });

  return positions;
};

const getBlockDropZones = (blockElements: Element[]): DropZonePosition[] => {
  const positions: DropZonePosition[] = [];
  blockElements.forEach(element => {
    if (!isMeasurableElement(element)) return;

    if (element.getAttribute("data-no-drop-below") === "true") {
      return;
    }

    const rect = element.getBoundingClientRect();
    const { dataset } = element;
    const { accepts, rejects } = safeCategoryParse(
      dataset.accepts,
      dataset.rejects,
    );

    const orientation = getDropZoneOrientation(element);

    const isVertical = orientation === "vertical";
    const top = isVertical ? rect.top : rect.bottom - 8;
    const left = isVertical ? rect.right - 1 : rect.left;
    const width = isVertical ? 16 : rect.width;
    const height = isVertical ? rect.height : 16;

    positions.push({
      top,
      left,
      width,
      height,
      path: dataset.parentPath,
      index: parseInt(dataset.blockIndex || "0", 10) + 1,
      accepts,
      rejects,
      orientation,
    });
  });

  return positions;
};

export const createRecalculatePositions =
  (setDropZonePositions: SetDropZonePositions) => () => {
    const iframe = document.getElementById(IFRAME_ID);
    if (!(iframe instanceof HTMLIFrameElement) || !iframe?.contentDocument) {
      setDropZonePositions([]);
      return;
    }

    const listContainers = Array.from(
      iframe.contentDocument.querySelectorAll("[data-block-list-container]"),
    );
    const blockElements = Array.from(
      iframe.contentDocument.querySelectorAll("[data-measurable-block]"),
    );

    const emptyListPositions = getEmptyListDropZones(listContainers);
    const topDropZones = getTopDropZones(listContainers);
    const blockPositions = getBlockDropZones(blockElements);

    setDropZonePositions([
      ...emptyListPositions,
      ...topDropZones,
      ...blockPositions,
    ]);
  };

export const createDebouncedRecalculatePositions = (
  setDropZonePositions: SetDropZonePositions,
) => {
  const recalculatePositions = createRecalculatePositions(setDropZonePositions);
  return debounce(recalculatePositions, DEBOUNCE_TIME);
};
